import { generateClient } from "aws-amplify/api";
import {
  listCommanderPodPlayers,
  listCommanderPods,
  listCommanderRounds,
  listEventParticipants,
  listEventPlayerResults,
  listEventStandings,
  listEventTopCutResults,
  listMatches,
  listPlayerConstructs,
} from "../../graphql/queries";
import {
  deleteCommanderPod,
  deleteCommanderPodPlayer,
  deleteCommanderRound,
  deleteEvent,
  deleteEventParticipant,
  deleteEventPlayerResult,
  deleteEventStanding,
  deleteEventTopCutResult,
  deleteMatch,
  updatePlayerConstruct,
} from "../../graphql/mutations";

const client = generateClient();

const loadByEvent = async (query: string, field: string, eventId: string) => {
  const response: any = await client.graphql({
    query,
    variables: {
      filter: { eventId: { eq: eventId } },
      limit: 1000,
    },
  });
  return response.data[field].items;
};

export async function deleteEventCascade(eventId: string): Promise<void> {
  const [
    playerResults,
    topCutResults,
    standings,
    participants,
    matches,
    commanderPlayers,
    commanderPods,
    commanderRounds,
    constructsResponse,
  ] = await Promise.all([
    loadByEvent(listEventPlayerResults, "listEventPlayerResults", eventId),
    loadByEvent(listEventTopCutResults, "listEventTopCutResults", eventId),
    loadByEvent(listEventStandings, "listEventStandings", eventId),
    loadByEvent(listEventParticipants, "listEventParticipants", eventId),
    loadByEvent(listMatches, "listMatches", eventId),
    loadByEvent(listCommanderPodPlayers, "listCommanderPodPlayers", eventId),
    loadByEvent(listCommanderPods, "listCommanderPods", eventId),
    loadByEvent(listCommanderRounds, "listCommanderRounds", eventId),
    client.graphql({
      query: listPlayerConstructs,
      variables: { limit: 1000 },
    }),
  ]);
  const constructs = (constructsResponse as any).data.listPlayerConstructs
    .items;

  for (const result of playerResults) {
    await client.graphql({
      query: deleteEventPlayerResult,
      variables: { input: { id: result.id } },
    });
    const construct = constructs.find(
      (candidate: any) => candidate.id === result.playerConstructId
    );
    if (construct) {
      await client.graphql({
        query: updatePlayerConstruct,
        variables: {
          input: {
            id: construct.id,
            totalEvents: Math.max(0, construct.totalEvents - 1),
            totalPoints: Math.max(0, construct.totalPoints - result.points),
            totalWins: Math.max(0, construct.totalWins - result.wins),
            totalLosses: Math.max(0, construct.totalLosses - result.losses),
            totalDraws: Math.max(0, construct.totalDraws - result.draws),
            bestRank: null,
            averageRank: null,
          },
        },
      });
    }
  }

  for (const record of commanderPlayers) {
    await client.graphql({
      query: deleteCommanderPodPlayer,
      variables: { input: { id: record.id } },
    });
  }
  for (const record of commanderPods) {
    await client.graphql({
      query: deleteCommanderPod,
      variables: { input: { id: record.id } },
    });
  }
  for (const record of commanderRounds) {
    await client.graphql({
      query: deleteCommanderRound,
      variables: { input: { id: record.id } },
    });
  }
  for (const record of topCutResults) {
    await client.graphql({
      query: deleteEventTopCutResult,
      variables: { input: { id: record.id } },
    });
  }
  for (const record of standings) {
    await client.graphql({
      query: deleteEventStanding,
      variables: { input: { id: record.id } },
    });
  }
  for (const record of participants) {
    await client.graphql({
      query: deleteEventParticipant,
      variables: { input: { id: record.id } },
    });
  }
  for (const record of matches) {
    await client.graphql({
      query: deleteMatch,
      variables: { input: { id: record.id } },
    });
  }

  await client.graphql({
    query: deleteEvent,
    variables: { input: { id: eventId } },
  });
}
